import { RICH_FORMAT } from "@fg/richformat";

/**
 * Text colour, highlight and font size in a message travel as class names, never as inline styles
 * (the sanitiser strips `style`). The ids come from Shared/richformat/format.json, which the desktop
 * app reads too:
 *
 *   <span class="fg-c-red">  text colour      <mark class="fg-h-yellow">  highlight      <span class="fg-s-lg">  size
 *   <span class="fg-f-georgia">  font family
 *
 * The rules that paint the classes are in styles/richformat.css (a test keeps them in step with the JSON).
 */
export const COLORS = RICH_FORMAT.colors;
export const HIGHLIGHTS = RICH_FORMAT.highlights;
export const SIZES = RICH_FORMAT.sizes;
export const FONTS = RICH_FORMAT.fonts ?? [];
/** The fonts a menu offers: every font a message may carry except those marked `offered: false`. */
export const OFFERED_FONTS = FONTS.filter((f) => f.offered !== false);

export type FormatKind = "c" | "h" | "s" | "f";

const IDS: Record<FormatKind, ReadonlySet<string>> = {
    c: new Set(COLORS.map((c) => c.id)),
    h: new Set(HIGHLIGHTS.map((c) => c.id)),
    s: new Set(SIZES.map((c) => c.id)),
    f: new Set(FONTS.map((c) => c.id))
};

const CLASS = /^fg-([chsf])-([a-z0-9-]+)$/;

/** True for exactly `fg-c-<id>`, `fg-h-<id>`, `fg-s-<id>` or `fg-f-<id>` with an id from format.json. */
export function isFormatClass(cls: string): boolean {
    const m = CLASS.exec(cls);
    return Boolean(m && IDS[m[1] as FormatKind].has(m[2]!));
}

/** The kind ("c" | "h" | "s" | "f") and id of a format class, or null. */
export function parseFormatClass(cls: string): { kind: FormatKind; id: string } | null {
    const m = CLASS.exec(cls);
    if (!m || !IDS[m[1] as FormatKind].has(m[2]!)) return null;
    return { kind: m[1] as FormatKind, id: m[2]! };
}
